import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Folder, Loader2, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toast'
import {
  DOCUMENT_FILE_ACCEPT,
  MAX_DOCUMENT_SIZE_BYTES,
  isDocumentFileExtension,
} from '@/lib/constants'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import { fileNameExtension } from '@/lib/documentRename'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/services/api'
import { AttachmentPreviewGrid } from './AttachmentPreviewGrid'
import { FileUploadDropzone } from './FileUploadDropzone'
import type { AttachmentRef } from './types'
import type { AttachmentTarget } from '@/types'

const DOCUMENT_LIBRARY_OPTIONS_STALE_TIME = 1 * 60 * 1000 // 1 minute

export interface AttachFilesSectionProps {
  /** Controlled list of currently-attached files. */
  value: AttachmentRef[]
  onChange: (files: AttachmentRef[]) => void

  /** Restrict the upload input. Defaults to the Task document formats. */
  accept?: string

  /** Max size of one file. Defaults to the API's 10 MB document cap. */
  maxSizeBytes?: number

  /**
   * Optional second line under the upload hint describing accepted formats /
   * size (e.g. "PDF, JPG, PNG - 10 MB max per file"). Hidden when omitted.
   */
  uploadFormatsHint?: string

  /** Client whose documents back the "Client documents" tab. */
  clientId?: string

  /**
   * Service-call injections so each module supplies its own endpoints. When a
   * function is omitted, its tab is hidden.
   */
  uploadFn?: (file: File) => Promise<AttachmentRef>
  listClientDocsFn?: (clientId: string) => Promise<AttachmentRef[]>
  listLibraryFn?: () => Promise<AttachmentRef[]>
  /** Entity type used to cache document-library options. */
  libraryTarget?: AttachmentTarget
  /** Load document-library options before its tab is opened. */
  preloadLibrary?: boolean
  onRename?: (
    item: AttachmentRef,
    name: string
  ) => Promise<Partial<AttachmentRef> | void> | Partial<AttachmentRef> | void

  disabled?: boolean
  /** View-only mode: keep preview/download, hide add/upload/remove actions. */
  readOnly?: boolean
  /** Hide the built-in section heading when the parent renders one. */
  hideHeading?: boolean
  /** Hide the field label above the upload dropzone. */
  hideUploadLabel?: boolean
  /** Figma tag-toggle tab styling (orange pill group). */
  brandTabs?: boolean
  /** Optional label for the upload source tab. */
  uploadTabLabel?: string
  /**
   * Detail-page mode: hide source tabs and the dashed upload dropzone; the
   * parent supplies an external Upload trigger via `onUploadReady`.
   */
  compact?: boolean
  /** Called once the hidden file input is mounted so parents can open it. */
  onUploadReady?: (openUpload: () => void) => void
  /** Fired when an upload starts or finishes. */
  onUploadingChange?: (uploading: boolean) => void
  /** Empty-state heading shown when no files are attached. */
  emptyMessage?: string
  /** Optional supporting copy for the Figma attachment empty state. */
  emptyDescription?: string
  emptyMessageClassName?: string
  emptyDescriptionClassName?: string
  /** Open an attached document when its preview is selected. */
  openPreviewOnClick?: boolean
}

type SourceTab = 'upload' | 'client' | 'library'

const tabTriggerClass =
  'shrink-0 rounded-lg border-0 px-3 py-1.5 text-sm font-medium leading-[1.4] text-[#3d434e] shadow-none data-[state=active]:border-transparent data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white data-[state=active]:shadow-none'

// Identity key so the same document can't be attached twice.
function keyOf(a: AttachmentRef): string {
  return a.file_id ?? a.id ?? `${a.source}:${a.name}:${a.url ?? ''}`
}

// Multi-source attachment picker: upload a new file, pick from the client's
// documents, or pick from the Settings document library. Each source is wired
// via an injected service function so every module (jobs #13, quotes #31,
// invoices #44, contracts #59, technicians #70, settings #75, #32) can supply
// its own calls. Renders selected files as Figma preview cards.
export function AttachFilesSection({
  value,
  onChange,
  accept = DOCUMENT_FILE_ACCEPT,
  maxSizeBytes = MAX_DOCUMENT_SIZE_BYTES,
  uploadFormatsHint,
  clientId,
  uploadFn,
  listClientDocsFn,
  listLibraryFn,
  libraryTarget,
  preloadLibrary = false,
  onRename,
  disabled,
  readOnly = false,
  hideHeading = false,
  hideUploadLabel = false,
  brandTabs = false,
  uploadTabLabel,
  compact = false,
  onUploadReady,
  onUploadingChange,
  emptyMessage,
  emptyDescription,
  emptyMessageClassName,
  emptyDescriptionClassName,
  openPreviewOnClick = false,
}: AttachFilesSectionProps) {
  const { t } = useTranslation('shared')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [clientDocs, setClientDocs] = useState<AttachmentRef[] | null>(null)
  const [loadingClient, setLoadingClient] = useState(false)

  const tabs: SourceTab[] = [
    ...(uploadFn ? (['upload'] as const) : []),
    ...(listClientDocsFn ? (['client'] as const) : []),
    ...(listLibraryFn && libraryTarget ? (['library'] as const) : []),
  ]

  useEffect(() => {
    onUploadingChange?.(uploading)
  }, [onUploadingChange, uploading])

  useEffect(() => {
    if (!uploadFn || readOnly) return
    onUploadReady?.(() => {
      if (!uploading && !disabled) {
        fileInputRef.current?.click()
      }
    })
  }, [uploadFn, onUploadReady, disabled, readOnly, uploading])

  const isBusy = !!disabled || uploading
  const canMutate = !readOnly && !disabled
  const [active, setActive] = useState<SourceTab>(tabs[0] ?? 'upload')

  const shouldLoadLibrary =
    !!listLibraryFn &&
    !!libraryTarget &&
    (active === 'library' || (preloadLibrary && !readOnly && !compact))
  const { data: libraryDocs = null, isLoading: loadingLibrary } = useQuery({
    queryKey: ['settings', 'document-library', 'options', libraryTarget],
    queryFn: () => listLibraryFn!(),
    enabled: shouldLoadLibrary,
    staleTime: DOCUMENT_LIBRARY_OPTIONS_STALE_TIME,
    retry: false,
  })

  // Client documents stay lazy-loaded the first time their tab is opened.
  // The loading flag is flipped here to drive the spinner before the async
  // fetch resolves; this is the documented "fetch in effect" exception.
  useEffect(() => {
    if (active === 'client' && listClientDocsFn && clientId && !clientDocs) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoadingClient(true)
      listClientDocsFn(clientId)
        .then(setClientDocs)
        .catch(() => setClientDocs([]))
        .finally(() => setLoadingClient(false))
    }
  }, [active, clientId, clientDocs, listClientDocsFn])

  const selectedKeys = new Set(value.map(keyOf))

  const addAttachment = (a: AttachmentRef) => {
    if (selectedKeys.has(keyOf(a))) return
    onChange([...value, a])
  }
  const removeAttachment = (a: AttachmentRef) =>
    onChange(value.filter(v => keyOf(v) !== keyOf(a)))
  const renameAttachment = async (item: AttachmentRef, name: string) => {
    const updated = await onRename?.(item, name)
    const fileName = updated?.name ?? `${name}${fileNameExtension(item.name)}`
    const file = item.file
      ? new File([item.file], fileName, {
          type: item.file.type,
          lastModified: item.file.lastModified,
        })
      : item.file

    onChange(
      value.map(current =>
        keyOf(current) === keyOf(item)
          ? { ...current, ...updated, name: fileName, file }
          : current
      )
    )
  }
  const openAttachment = (item: AttachmentRef) => {
    if (!item.url) return
    window.open(item.url, '_blank', 'noopener,noreferrer')
  }

  const processFiles = async (files: FileList | File[]) => {
    if (!uploadFn || files.length === 0 || uploading || disabled || readOnly)
      return

    const attachedFiles: AttachmentRef[] = []
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
        if (!isDocumentFileExtension(extension)) {
          toast({
            variant: 'destructive',
            title: t('attachFiles.unsupportedFormat'),
          })
          continue
        }
        if (isFileTooLarge(file, maxSizeBytes)) {
          toast({
            variant: 'destructive',
            title: t('attachFiles.tooLarge', {
              max: getFileSizeLimitMb(maxSizeBytes),
            }),
          })
          continue
        }

        try {
          attachedFiles.push(await uploadFn(file))
        } catch (error) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(error, t('attachFiles.uploadError')),
          })
        }
      }

      if (attachedFiles.length > 0) {
        const knownKeys = new Set(selectedKeys)
        const uniqueFiles = attachedFiles.filter(file => {
          const key = keyOf(file)
          if (knownKeys.has(key)) return false
          knownKeys.add(key)
          return true
        })
        if (uniqueFiles.length > 0) onChange([...value, ...uniqueFiles])
        if (attachedFiles.some(file => !file.file)) {
          toast({ variant: 'success', title: t('attachFiles.uploadSuccess') })
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length > 0) void processFiles(files)
  }

  const renderPickList = (
    docs: AttachmentRef[] | null,
    loading: boolean,
    loadingLabel: string,
    emptyLabel: string
  ) => {
    if (loading) {
      return <p className='py-3 text-sm text-[#3d434e]'>{loadingLabel}</p>
    }
    if (!docs || docs.length === 0) {
      return <p className='py-3 text-sm text-[#3d434e]'>{emptyLabel}</p>
    }
    return (
      <ul className='space-y-2'>
        {docs.map(doc => {
          const picked = selectedKeys.has(keyOf(doc))
          return (
            <li
              key={keyOf(doc)}
              className='flex items-center justify-between rounded-xl border border-[#e7e9ef] px-3 py-2 text-sm'
            >
              <span className='flex items-center gap-2 truncate'>
                <Paperclip className='h-4 w-4 shrink-0 text-[#8f95a6]' />
                <span className='truncate text-[#171a20]'>{doc.name}</span>
              </span>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={!canMutate || isBusy || picked}
                onClick={() => addAttachment(doc)}
              >
                {t('attachFiles.add')}
              </Button>
            </li>
          )
        })}
      </ul>
    )
  }

  const dropzone = (
    <FileUploadDropzone
      className={cn(
        brandTabs &&
          'gap-4 rounded-xl [&>span:last-child>span]:leading-4 [&>span:last-child>span]:text-[#3d434e] [&>span:last-child>span+span]:mt-0'
      )}
      disabled={disabled || uploading || readOnly}
      onClick={() => fileInputRef.current?.click()}
      dragActive={dragActive}
      onDragEnter={event => {
        event.preventDefault()
        setDragActive(true)
      }}
      onDragOver={event => {
        event.preventDefault()
        setDragActive(true)
      }}
      onDragLeave={event => {
        event.preventDefault()
        setDragActive(false)
      }}
      onDrop={event => {
        event.preventDefault()
        setDragActive(false)
        if (disabled || uploading || readOnly) return
        const files = event.dataTransfer.files
        if (files.length > 0) void processFiles(files)
      }}
      primaryText={t('attachFiles.uploadHintPrimary')}
      secondaryText={uploadFormatsHint ?? t('attachFiles.uploadHintFormats')}
      loading={uploading}
      loadingText={t('attachFiles.uploading')}
    />
  )

  return (
    <div className='flex flex-col gap-4'>
      {!hideHeading && (
        <h3 className='text-sm font-semibold text-[#171a20]'>
          {t('attachFiles.heading')}
        </h3>
      )}

      {uploadFn && (
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={accept}
          className='hidden'
          disabled={isBusy || readOnly}
          onChange={handleUpload}
        />
      )}

      {tabs.length > 0 && !compact && (
        <Tabs
          value={active}
          onValueChange={v => !isBusy && setActive(v as SourceTab)}
        >
          <TabsList
            className={cn(
              'h-auto gap-2 overflow-clip rounded-lg border-0 bg-[#f4f5f8] p-1 shadow-[0_1px_4px_rgba(38,45,66,0.05)]',
              brandTabs ? 'max-w-full overflow-x-auto' : 'w-fit',
              !brandTabs && 'bg-[#f4f5f8]'
            )}
          >
            {uploadFn && (
              <TabsTrigger value='upload' className={tabTriggerClass}>
                {uploadTabLabel ?? t('attachFiles.uploadNew')}
              </TabsTrigger>
            )}
            {listClientDocsFn && (
              <TabsTrigger value='client' className={tabTriggerClass}>
                {t('attachFiles.fromClient')}
              </TabsTrigger>
            )}
            {listLibraryFn && libraryTarget && (
              <TabsTrigger value='library' className={tabTriggerClass}>
                {t('attachFiles.fromLibrary')}
              </TabsTrigger>
            )}
          </TabsList>

          {uploadFn && (
            <TabsContent value='upload' className='mt-4 flex flex-col gap-1'>
              {!hideUploadLabel && (
                <p className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'>
                  {t('attachFiles.uploadLabel')}
                  <span className='text-[#d63b33]'>*</span>
                </p>
              )}
              {dropzone}
            </TabsContent>
          )}

          {listClientDocsFn && (
            <TabsContent value='client'>
              {clientId ? (
                renderPickList(
                  clientDocs,
                  loadingClient,
                  t('attachFiles.loadingClientDocs'),
                  t('attachFiles.noClientDocs')
                )
              ) : (
                <p className='py-3 text-sm text-[#3d434e]'>
                  {t('attachFiles.noClientSelected')}
                </p>
              )}
            </TabsContent>
          )}

          {listLibraryFn && libraryTarget && (
            <TabsContent value='library'>
              {renderPickList(
                libraryDocs,
                loadingLibrary,
                t('attachFiles.loadingLibrary'),
                t('attachFiles.noLibrary')
              )}
            </TabsContent>
          )}
        </Tabs>
      )}

      <div className={cn(compact && 'relative min-h-16')}>
        {compact && uploading && (
          <div
            className='absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-lg bg-white/80'
            aria-live='polite'
            aria-busy
          >
            <Loader2 className='h-5 w-5 animate-spin text-[#ff5a1f]' />
            <span className='text-sm text-[#3d434e]'>
              {t('attachFiles.uploading')}
            </span>
          </div>
        )}
        {value.length === 0 ? (
          brandTabs && emptyMessage ? (
            <div className='flex min-h-[148px] w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-xl bg-[#f4f5f8] p-6 text-center'>
              <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#171a20]'>
                <Folder className='size-5' />
              </span>
              <div className='flex flex-col items-center gap-1'>
                <p
                  className={cn(
                    'font-heading text-base font-semibold leading-[1.2] text-[#171a20]',
                    emptyMessageClassName
                  )}
                >
                  {emptyMessage}
                </p>
                {emptyDescription ? (
                  <p
                    className={cn(
                      'text-sm font-normal leading-[1.4] text-[#6c7285]',
                      emptyDescriptionClassName
                    )}
                  >
                    {emptyDescription}
                  </p>
                ) : null}
              </div>
            </div>
          ) : compact || emptyMessage ? (
            <p
              className={cn(
                'text-base leading-[1.4] text-[#3d434e]',
                uploading && 'opacity-50'
              )}
            >
              {emptyMessage ?? t('attachFiles.noneSelected')}
            </p>
          ) : null
        ) : (
          <div
            className={cn(
              uploading && compact && 'pointer-events-none opacity-50'
            )}
          >
            <AttachmentPreviewGrid
              items={value}
              removeLabel={t('attachFiles.remove')}
              onRemove={!compact && canMutate ? removeAttachment : undefined}
              onRename={
                !compact && canMutate && onRename ? renameAttachment : undefined
              }
              disabled={isBusy}
              onItemClick={
                readOnly || compact || openPreviewOnClick
                  ? openAttachment
                  : undefined
              }
              itemClickLabel={
                readOnly || compact || openPreviewOnClick
                  ? t('attachFiles.view')
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default AttachFilesSection
